import React, { useCallback, useEffect, useMemo, useRef, useState, Suspense } from "react"
import { toast } from "sonner"
import { loadAppV2, detectSchemaVersion, loadFromSchemaUrl, type LoadedAppV2 } from "./app-loader"
type ToastParams = { message: string; description?: string; variant?: string }
import { useAuth } from "./auth/auth-context"
import { useCogoWebMCP } from "@cogo/webmcp-runtime/react"

// Lazy-load v2 renderer to avoid bundling both renderers when only one is needed
const AppV2Lazy = React.lazy(() =>
  import("@cogo/react-renderer-v2").then((m) => ({ default: m.AppV2 }))
)
const CogoAgentLazy = React.lazy(() =>
  import("@cogo/react-renderer-v2").then((m) => ({ default: m.CogoAgent }))
)

declare global {
  interface Window {
    __COGO_APP__?: { slug: string; schemaUrl: string }
  }
}

type TenantColorScheme = "light" | "dark"

export function resolveTenantColorScheme(
  search = typeof window === "undefined" ? "" : window.location.search,
): TenantColorScheme {
  const theme = new URLSearchParams(search).get("theme")?.toLowerCase()
  if (theme === "dark") return "dark"
  if (
    theme === "system" &&
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: dark)").matches
  ) {
    return "dark"
  }
  return "light"
}

// Phase 1.5 — agentic compose runtime route helper.
// Parses URL hash `#compose=<base64-or-encoded-json>` into the agentic spec shape
// returned by POST /api/agentic/compose. Returns null when the hash is absent or
// malformed so the caller can fall through to normal page resolution.
type ComposeSpec = { blocks: string[]; layout: "single" | "stack" | "2col" | "grid"; context: Record<string, unknown> }
function parseComposeHash(): ComposeSpec | null {
  if (typeof window === "undefined") return null
  const hash = window.location.hash
  if (!hash.startsWith("#compose=")) return null
  const raw = hash.slice("#compose=".length)
  if (!raw) return null
  try {
    // Accept either raw URL-encoded JSON or base64-encoded JSON. Base64 is preferred
    // because compose specs from the API can contain arbitrary context bytes that bloat
    // URLs when percent-encoded.
    let decoded: string
    try {
      decoded = atob(decodeURIComponent(raw))
    } catch {
      decoded = decodeURIComponent(raw)
    }
    const parsed = JSON.parse(decoded)
    if (!parsed || !Array.isArray(parsed.blocks) || parsed.blocks.length === 0) return null
    const layout = ["single", "stack", "2col", "grid"].includes(parsed.layout) ? parsed.layout : "single"
    return {
      blocks: parsed.blocks.filter((b: unknown) => typeof b === "string"),
      layout,
      context: typeof parsed.context === "object" && parsed.context ? parsed.context : {},
    }
  } catch {
    return null
  }
}

const VerifyPage = React.lazy(() => import("./pages/VerifyPage"))
// RN Preview removed — react-native-web/expo incompatible with Vite 8 Rolldown. Use Expo native build instead.
// const RnPreviewPage = React.lazy(() => import("./pages/RnPreviewPage"))
const ComparePage = React.lazy(() => import("./pages/ComparePage"))
const HomePage = React.lazy(() => import("./pages/homepage/HomePage"))
const AuthPage = React.lazy(() => import("./auth/auth-page").then((m) => ({ default: m.AuthPage })))
const AuthCallback = React.lazy(() => import("./auth/auth-callback").then((m) => ({ default: m.AuthCallback })))
const OnboardingPage = React.lazy(() => import("./auth/onboarding-page").then((m) => ({ default: m.OnboardingPage })))
const V4RefApp = React.lazy(() => import("./v4-reference/V4RefApp").then((m) => ({ default: m.V4RefApp })))
const MeasureRoute = React.lazy(() => import("./measure-route").then((m) => ({ default: m.MeasureRoute })))

/**
 * Route prefix → template directory mapping.
 * Only validated templates (score ≥ 80) are included.
 * Exported so smoke tests can verify each entry has files on disk
 * without maintaining a stale mirror copy.
 */
export const TEMPLATE_MAP: Record<string, string> = {
  // ── v2 Templates (schemaVersion 3.0) ─────────────────────────
  // E-Commerce
  "/store": "fashion-creator-store-new3",
  "/petshop": "pet-supply-store-new3",
  "/fashion": "fashion-creator-store-new3",
  "/kbeauty": "k-beauty-store-new3",
  "/coffee": "coffee-shop-store-new3",
  "/tech": "tech-gadget-store-new3",
  "/fitness": "fitness-gym-new3",
  "/food": "food-delivery-new3",
  "/bakery": "bakery-shop-new3",
  "/candles": "candle-shop-new3",
  "/flowers": "flower-shop-new3",
  "/tea": "tea-shop-new3",
  "/plants": "plant-nursery-new3",
  "/jewelry": "jewelry-new3",
  "/yoga": "yoga-studio-new3",
  "/ecommerce": "ecommerce-storefront-new3",
  // Food & Beverage
  "/coffeeshop": "coffee-shop-new3",
  "/premium-tea": "premium-tea-shop-new3",
  "/premium-coffee": "premium-coffee-shop-new3",
  "/craft-coffee": "premium-craft-coffee-shop-new3",
  "/recipes": "food-recipe-blog-new3",
  "/recipe-blog": "recipe-blog-new3",
  "/restaurant": "restaurant-ordering-system-new3",
  // SaaS & Landing
  "/agency": "agency-studio-new3",
  "/saas-analytics": "saas-analytics-new3",
  "/helpdesk": "help-center-new3",
  "/inventory": "inventory-management-new3",
  // Portfolio
  "/devfolio": "dev-portfolio-new3",
  "/photo": "photography-portfolio-new3",
  // Content
  "/cblog": "creator-blog-new3",
  "/blog": "blog-new3",
  "/techblog": "tech-blog-new3",
  // Dashboard & Admin
  "/analytics": "analytics-dashboard-new3",
  "/crm": "crm-admin-new3",
  "/tracker": "project-tracker-new3",
  "/saas-dash": "saas-dashboard-new3",
  "/admin": "admin-panel-new3",
  "/tasks": "task-manager-new3",
  "/pm": "project-management-new3",
  "/hr": "hr-new3",
  "/analytics2": "analytics-new3",
  "/crm2": "crm-new3",
  "/pm-tool": "project-management-tool-new3",
  "/crm-panel": "crm-admin-panel-new3",
  "/saas-dash2": "saas-analytics-dashboard-new3",
  "/fitness-track": "fitness-tracker-new3",
  "/petsupply": "pet-supply-new3",
  "/ecom": "e-commerce-new3",
}

/** Detect base path at runtime from the module script's src attribute.
 *  Vite inlines import.meta.env.BASE_URL at build-time, but when cogo-studio
 *  copies the starter dist into /preview/ the inlined value is still "/".
 *  Deriving it from the actual <script> tag works regardless of where the
 *  bundle is served from. Falls back to import.meta.env.BASE_URL. */
const BASE_PATH = (() => {
  // In a browser, find our own <script type="module"> and extract base from its src
  if (typeof document !== "undefined") {
    const scripts = document.querySelectorAll('script[type="module"][src]')
    for (const s of scripts) {
      const src = s.getAttribute("src") || ""
      // Match e.g. "/preview/assets/index-xxx.js" → "/preview"
      const m = src.match(/^(\/[^/]+)\/assets\//)
      if (m) return m[1] // e.g. "/preview"
    }
  }
  return import.meta.env.BASE_URL.replace(/\/$/, "")
})()
function stripBase(pathname: string): string {
  if (BASE_PATH && pathname.startsWith(BASE_PATH)) {
    return pathname.slice(BASE_PATH.length) || "/"
  }
  return pathname
}
function withBase(route: string): string {
  return BASE_PATH + route
}

/**
 * Canonical template route: /templates/{templateId}
 * e.g. /templates/fashion-creator-store-new3
 *      /templates/fashion-creator-store-new3/product/1
 * Legacy short prefixes (/fashion, /coffee, ...) still supported for backwards compat.
 */
const TEMPLATES_PREFIX = "/templates/"

function resolveTemplate(pathname: string): string | null {
  // Canonical: /templates/{templateId}[/subRoute]
  if (pathname.startsWith(TEMPLATES_PREFIX)) {
    const rest = pathname.slice(TEMPLATES_PREFIX.length)
    const id = rest.split("/")[0]
    return id || null
  }
  // Legacy short prefixes
  for (const [prefix, template] of Object.entries(TEMPLATE_MAP)) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) {
      return template
    }
  }
  return null
}

/** Find the route prefix for a given pathname. */
function resolvePrefix(pathname: string): string | null {
  // Canonical: /templates/{templateId}
  if (pathname.startsWith(TEMPLATES_PREFIX)) {
    const rest = pathname.slice(TEMPLATES_PREFIX.length)
    const id = rest.split("/")[0]
    if (id) return TEMPLATES_PREFIX + id
  }
  // Legacy short prefixes
  for (const prefix of Object.keys(TEMPLATE_MAP)) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) {
      return prefix
    }
  }
  return null
}

/** Strip the template prefix from the browser path to get the app-internal route.
 *  e.g. "/food" → "/", "/food/cart" → "/cart" */
function stripPrefix(pathname: string, prefix: string): string {
  if (pathname === prefix) return "/"
  return pathname.slice(prefix.length) || "/"
}

type TemplateCategory = {
  label: string
  items: { route: string; label: string; desc: string; blocks: number }[]
}

const TEMPLATE_CATEGORIES: TemplateCategory[] = [
  {
    label: "E-Commerce",
    items: [
      { route: "/templates/fashion-creator-store-new3", label: "MUSE — Fashion Creator Store", desc: "Editorial fashion store with lookbook hero, curated collections, and cart.", blocks: 9 },
      { route: "/templates/k-beauty-store-new3", label: "GLOW — K-Beauty Store", desc: "Korean beauty with skincare routines, bestsellers, and ingredient details.", blocks: 8 },
      { route: "/templates/pet-supply-store-new3", label: "PawPal — Pet Supply Store", desc: "Pet supply store with product catalog, cart, and checkout.", blocks: 8 },
      { route: "/templates/tech-gadget-store-new3", label: "ByteGear — Tech Gadgets", desc: "Electronics store with earbuds, watches, keyboards, and accessories.", blocks: 7 },
      { route: "/templates/jewelry-new3", label: "Luxe Aura — Fine Jewelry", desc: "Fine jewelry boutique with collections, product details, and wishlist.", blocks: 7 },
      { route: "/templates/candle-shop-new3", label: "Lumina Candles — Artisanal Shop", desc: "Artisanal scented candles with collections and fragrance profiles.", blocks: 7 },
      { route: "/templates/flower-shop-new3", label: "Flower Shop", desc: "Fresh flower arrangements, bouquets, and same-day delivery.", blocks: 7 },
      { route: "/templates/plant-nursery-new3", label: "GreenThumb — Plant Nursery", desc: "Indoor and outdoor plants with care guides and plant finder.", blocks: 7 },
      { route: "/templates/tea-shop-new3", label: "Leaf & Kettle — Premium Tea", desc: "Premium loose leaf teas with brewing guides and subscriptions.", blocks: 7 },
      { route: "/templates/ecommerce-storefront-new3", label: "ShopWave — Modern Store", desc: "Modern e-commerce storefront with product grid and checkout.", blocks: 5 },
      { route: "/templates/e-commerce-new3", label: "E-Commerce", desc: "General e-commerce template with product catalog.", blocks: 5 },
      { route: "/templates/pet-supply-new3", label: "Pet Supply", desc: "Pet supply store with product categories.", blocks: 4 },
    ],
  },
  {
    label: "Food & Beverage",
    items: [
      { route: "/templates/coffee-shop-store-new3", label: "BrewHaus — Craft Coffee", desc: "Artisan coffee shop with beverages, pastries, beans, and gear.", blocks: 7 },
      { route: "/templates/bakery-shop-new3", label: "The Golden Crust Bakery", desc: "Artisan bakery with fresh breads, pastries, and custom cake orders.", blocks: 7 },
      { route: "/templates/food-delivery-new3", label: "NomNom — Food Delivery", desc: "Food delivery with burgers, sushi, pasta, pizza, and healthy bowls.", blocks: 7 },
      { route: "/templates/premium-tea-shop-new3", label: "Premium Tea Shop", desc: "Tea collection with tasting notes and subscription options.", blocks: 5 },
      { route: "/templates/premium-coffee-shop-new3", label: "Premium Coffee Shop", desc: "Specialty coffee with brewing methods and bean origins.", blocks: 5 },
      { route: "/templates/premium-craft-coffee-shop-new3", label: "Premium Craft Coffee", desc: "Craft coffee roastery with cupping sessions.", blocks: 4 },
      { route: "/templates/coffee-shop-new3", label: "Coffee Shop", desc: "Coffee shop with menu and locations.", blocks: 5 },
      { route: "/templates/food-recipe-blog-new3", label: "Food Recipe Blog", desc: "Recipe blog with categories, cooking tips, and meal plans.", blocks: 4 },
      { route: "/templates/recipe-blog-new3", label: "Recipe Blog", desc: "Recipe collection with search and categories.", blocks: 3 },
      { route: "/templates/restaurant-ordering-system-new3", label: "Restaurant Ordering", desc: "Restaurant ordering system with menu and checkout.", blocks: 4 },
    ],
  },
  {
    label: "Health & Fitness",
    items: [
      { route: "/templates/fitness-gym-new3", label: "FitZone — Fitness & Wellness", desc: "Gym classes, personal training, memberships, and nutrition.", blocks: 8 },
      { route: "/templates/yoga-studio-new3", label: "ZenFlow Yoga Studio", desc: "Yoga classes, schedules, instructor profiles, and membership plans.", blocks: 7 },
      { route: "/templates/fitness-tracker-new3", label: "Fitness Tracker", desc: "Workout tracker with progress charts and goals.", blocks: 4 },
    ],
  },
  {
    label: "SaaS & Landing",
    items: [
      { route: "/templates/agency-studio-new3", label: "STUDIO — Creative Agency", desc: "Agency portfolio with services, work showcase, process timeline, and contact.", blocks: 7 },
      { route: "/templates/saas-analytics-new3", label: "SaaS Analytics", desc: "SaaS analytics landing with feature highlights and pricing.", blocks: 5 },
      { route: "/templates/saas-analytics-dashboard-new3", label: "SaaS Analytics Dashboard", desc: "SaaS metrics dashboard with funnel and retention charts.", blocks: 4 },
      { route: "/templates/inventory-management-new3", label: "StockPro — Inventory Manager", desc: "Inventory management with stock tracking and alerts.", blocks: 5 },
      { route: "/templates/help-center-new3", label: "HelpDesk — Knowledge Base", desc: "Help center with FAQs, search, and article categories.", blocks: 4 },
    ],
  },
  {
    label: "Portfolio & Photography",
    items: [
      { route: "/templates/dev-portfolio-new3", label: "Alex Chen — Dev Portfolio", desc: "Terminal-style hero, tech stack, project cards, and contact form.", blocks: 7 },
      { route: "/templates/photography-portfolio-new3", label: "Lens & Light — Photography", desc: "Photography portfolio with gallery, lightbox, and booking.", blocks: 7 },
    ],
  },
  {
    label: "Content & Blog",
    items: [
      { route: "/templates/creator-blog-new3", label: "Inkwell — Creator Blog", desc: "Typography-focused blog with articles, categories, and newsletter.", blocks: 7 },
      { route: "/templates/blog-new3", label: "Blog", desc: "Blog template with posts, categories, and comments.", blocks: 4 },
      { route: "/templates/tech-blog-new3", label: "Tech Blog", desc: "Developer-focused blog with code snippets and tutorials.", blocks: 4 },
    ],
  },
  {
    label: "Dashboard & Admin",
    items: [
      { route: "/templates/commerce-admin", label: "Commerce Admin", desc: "Agentic admin — 244 pages, sidebar nav, membership, bookings, financials, inventory.", blocks: 149 },
      { route: "/templates/analytics-dashboard-new3", label: "Analytics Pro — Dashboard", desc: "KPI cards, revenue/traffic charts, data tables, date-range reports.", blocks: 15 },
      { route: "/templates/project-management-new3", label: "Project Management", desc: "Project management with boards, timelines, and team overview.", blocks: 11 },
      { route: "/templates/saas-dashboard-new3", label: "SaaS Metrics — Dashboard", desc: "SaaS analytics with MRR/churn charts, customer cohort tables.", blocks: 10 },
      { route: "/templates/task-manager-new3", label: "TaskPro — Project Manager", desc: "Task tables, burndown/workload charts, sprint progress.", blocks: 9 },
      { route: "/templates/crm-admin-new3", label: "CRM Pro — Admin Panel", desc: "Contact management, deal pipeline, form dialogs, settings.", blocks: 8 },
      { route: "/templates/admin-panel-new3", label: "AdminHub — Control Panel", desc: "Admin panel with user/order data tables, CRUD forms.", blocks: 8 },
      { route: "/templates/project-tracker-new3", label: "ProjectFlow — Tracker", desc: "Kanban task board, milestone timeline, team workload chart.", blocks: 7 },
      { route: "/templates/hr-new3", label: "HR Dashboard", desc: "HR dashboard with employee directory and leave management.", blocks: 5 },
      { route: "/templates/analytics-new3", label: "Analytics", desc: "Analytics dashboard with charts and reports.", blocks: 5 },
      { route: "/templates/crm-new3", label: "CRM", desc: "Customer relationship management with contacts and deals.", blocks: 5 },
      { route: "/templates/project-management-tool-new3", label: "Project Management Tool", desc: "Project management tool with task tracking.", blocks: 4 },
      { route: "/templates/crm-admin-panel-new3", label: "CRM Admin Panel", desc: "CRM admin panel with data views.", blocks: 3 },
    ],
  },
]

function TemplateLanding({ onNavigate }: { onNavigate: (route: string) => void }) {
  /** Derive thumbnail from template route, e.g. "/templates/fashion-creator-store-new3" → "/thumbnails/fashion-creator-store-new3.png" */
  const thumbFor = (route: string) => {
    const id = route.startsWith(TEMPLATES_PREFIX) ? route.slice(TEMPLATES_PREFIX.length).split("/")[0] : route.replace(/^\//, "")
    return `/thumbnails/${id}.png`
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center p-8">
      <div className="max-w-6xl w-full">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold mb-2">cogo Template Gallery</h1>
          <p className="text-muted-foreground">
            {TEMPLATE_CATEGORIES.reduce((n, c) => n + c.items.length, 0)} templates — powered by cogo JSON runtime
          </p>
        </div>

        <div className="space-y-10">
          {TEMPLATE_CATEGORIES.map((cat) => (
            <section key={cat.label}>
              <h2 className="text-lg font-semibold text-muted-foreground mb-4 border-b border-border pb-2">
                {cat.label}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {cat.items.map((t) => (
                  <button
                    key={t.route}
                    onClick={() => onNavigate(t.route)}
                    className="group flex flex-col overflow-hidden rounded-xl border bg-card hover:border-primary hover:shadow-lg transition-all text-left"
                  >
                    <div className="relative aspect-video w-full overflow-hidden bg-muted">
                      <img
                        src={thumbFor(t.route)}
                        alt={t.label}
                        loading="lazy"
                        className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = "none" }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center text-muted-foreground/40 text-3xl font-bold pointer-events-none">
                        {t.label.split("—")[0].trim().charAt(0)}
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="flex flex-col gap-1 p-3">
                      <div className="flex items-center justify-between w-full">
                        <span className="font-semibold text-sm">{t.label}</span>
                        <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                          {t.blocks} blocks
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground leading-snug line-clamp-2">{t.desc}</span>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  )
}

/** Tenant mode — renders a single app from __COGO_APP__ schema URL */
function TenantApp({ config }: { config: { slug: string; schemaUrl: string } }) {
  const { onRuntimeReady } = useCogoWebMCP()
  const [currentRoute, setCurrentRoute] = useState(window.location.pathname)
  const [appV2, setAppV2] = useState<LoadedAppV2 | null>(null)
  const [error, setError] = useState<string | null>(null)
  const colorScheme = useMemo(() => resolveTenantColorScheme(), [])

  useEffect(() => {
    document.documentElement.classList.toggle("dark", colorScheme === "dark")
    document.documentElement.style.colorScheme = colorScheme

    return () => {
      document.documentElement.classList.remove("dark")
      document.documentElement.style.colorScheme = ""
    }
  }, [colorScheme])

  useEffect(() => {
    loadFromSchemaUrl(config.schemaUrl)
      .then((loaded) => {
        setAppV2({
          manifest: loaded.manifest as unknown as import("@cogo/types-v2").App,
          blocks: loaded.blocks as unknown as Record<string, import("@cogo/types-v2").BlockDef>,
          schemaVersion: "3.0",
          renderPackBytes: loaded.renderPackBytes,
        })
      })
      .catch((err) => setError(err.message))
  }, [config.schemaUrl])

  useEffect(() => {
    const onPopState = () => setCurrentRoute(window.location.pathname)
    window.addEventListener("popstate", onPopState)
    return () => window.removeEventListener("popstate", onPopState)
  }, [])

  const handleNavigate = useCallback((route: string) => {
    setCurrentRoute(route)
    window.history.pushState(null, "", route)
    window.scrollTo(0, 0)
  }, [])

  const handleToast = useCallback((params: ToastParams) => {
    if (params.variant === "success") {
      toast.success(params.message, { description: params.description })
    } else if (params.variant === "error" || params.variant === "destructive") {
      toast.error(params.message, { description: params.description })
    } else {
      toast(params.message, { description: params.description })
    }
  }, [])

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center text-destructive">
        Failed to load app: {error}
      </div>
    )
  }
  if (!appV2) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        Loading...
      </div>
    )
  }

  return (
    <>
      <Suspense fallback={<div className="flex h-screen items-center justify-center text-muted-foreground">Loading...</div>}>
        <AppV2Lazy
          manifest={appV2.manifest}
          blocks={appV2.blocks}
          onToast={handleToast}
          initialRoute={currentRoute}
          onNavigate={handleNavigate}
          colorScheme={colorScheme}
          renderPackBytes={appV2.renderPackBytes}
        />
      </Suspense>
      <Suspense fallback={null}>
        <CogoAgentLazy
          apiKey={import.meta.env.VITE_GEMINI_API_KEY || ''}
          appId={appV2.manifest.appId}
          appName={appV2.manifest.name}
        />
      </Suspense>
    </>
  )
}

export function App() {
  // WebMCP hook — must be called before any early return (Rules of Hooks)
  const { onRuntimeReady } = useCogoWebMCP()

  // Tenant mode: __COGO_APP__ injected by Cloudflare tenant router
  const tenantConfig = window.__COGO_APP__
  if (tenantConfig) {
    return <TenantApp config={tenantConfig} />
  }

  // /rn-preview/{slug} → redirect to /app/{slug} (iOS web preview, Apple 4.7)
  const initialRoute = stripBase(window.location.pathname)
  if (initialRoute.startsWith("/rn-preview/")) {
    const slug = initialRoute.replace("/rn-preview/", "")
    window.location.replace(`/app/${slug}`)
    return null
  }

  return <AuthApp onRuntimeReady={onRuntimeReady} />
}

type AuthAppProps = {
  onRuntimeReady: ReturnType<typeof useCogoWebMCP>["onRuntimeReady"]
}

interface MemoizedIframeProps {
  iframeRef: React.RefObject<HTMLIFrameElement | null>
  src: string
  title: string
}

const MemoizedIframe = React.memo(
  ({ iframeRef, src, title }: MemoizedIframeProps) => {
    console.log(`[MemoizedIframe] Rendering iframe with src="${src}" title="${title}"`)
    return (
      <iframe
        ref={iframeRef}
        src={src}
        className="flex-1 w-full border-0"
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
        title={title}
      />
    )
  },
  (prevProps, nextProps) => {
    const identical = prevProps.src === nextProps.src && prevProps.title === nextProps.title
    console.log(`[MemoizedIframe] Comparing props: src="${prevProps.src}" vs "${nextProps.src}", identical=${identical}`)
    return identical
  }
)

function AuthApp({ onRuntimeReady }: AuthAppProps) {
  // Auth state for onboarding redirect
  const { needsOnboarding, isLoading: authLoading } = useAuth()

  // Dev/gallery mode: local template selection
  const [currentRoute, setCurrentRoute] = useState(stripBase(window.location.pathname))
  const [appV2, setAppV2] = useState<LoadedAppV2 | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [activeTemplate, setActiveTemplate] = useState<string | null>(null)

  const isVerifyPage = currentRoute === "/dev/verify"
  const isRnPreview = currentRoute.startsWith("/rn-preview/")
  const isComparePage = currentRoute === "/compare"
  const isLoginPage = currentRoute === "/login"
  const isAuthCallback = currentRoute === "/login/callback"
  const isOnboarding = currentRoute === "/onboarding"
  const isHomepage = currentRoute === "/homepage"
  const isV4Reference = currentRoute === "/v4-reference" || currentRoute.startsWith("/v4-reference/")
  // Phase A.2 measurement harness — /measure/:bundleId loads a single-block bundle
  // from /apps/measure/<bundleId>/. Used by Playwright probe in Task 8.
  const isMeasure = currentRoute === "/measure" || currentRoute.startsWith("/measure/")
  const measureBundleId = useMemo(() => {
    if (!isMeasure) return null
    const rest = currentRoute.slice("/measure".length).replace(/^\//, "")
    const id = rest.split("/")[0]
    return id || null
  }, [currentRoute, isMeasure])
  const isGallery = currentRoute === "/gallery" || currentRoute === "/"
  const templateId = useMemo(
    () => (isVerifyPage || isRnPreview || isComparePage || isLoginPage || isAuthCallback || isOnboarding || isHomepage || isV4Reference || isMeasure || isGallery ? null : resolveTemplate(currentRoute)),
    [currentRoute, isVerifyPage, isRnPreview, isComparePage, isLoginPage, isAuthCallback, isOnboarding, isHomepage, isV4Reference, isMeasure, isGallery],
  )

  const isEmbedded = window.self !== window.top

  // Phase 1.5 — agentic compose runtime route.
  // URL: /templates/{appId}#compose=<base64-encoded-spec-json>
  // Spec shape: { blocks: string[], layout: "single"|"stack"|"2col"|"grid", context?: object }
  // When present, override the loaded app's pages with a single synthetic compose page
  // so the user sees the agentic compose result rendered through the normal AppV2 pipeline.
  const composeSpec = useMemo(() => parseComposeHash(), [currentRoute])

  // Load template when route prefix changes — auto-detect v1/v2
  // Only load directly when embedded in iframe (Studio preview) — gallery uses its own iframe
  useEffect(() => {
    if (isVerifyPage || isRnPreview || isLoginPage || isAuthCallback || isOnboarding || isHomepage || isV4Reference || isMeasure || isGallery) return
    if (!templateId) {
      setAppV2(null)
      setActiveTemplate(null)
      return
    }
    // Gallery (not embedded): use iframe, skip direct loading
    if (!isEmbedded) return
    // Re-run when compose spec changes even if templateId hasn't (so updating the hash re-renders)
    if (templateId === activeTemplate && !composeSpec) return

    setAppV2(null)
    setError(null)
    const appPath = `${BASE_PATH}/apps/${templateId}`
    loadAppV2(appPath)
      .then((loaded) => {
        if (composeSpec && composeSpec.blocks.length > 0) {
          const synthetic: LoadedAppV2 = {
            ...loaded,
            manifest: {
              ...loaded.manifest,
              pages: [
                {
                  id: "compose",
                  route: "/compose",
                  layout: "stack",
                  blocks: composeSpec.blocks,
                  blockLayout: {},
                },
                ...loaded.manifest.pages,
              ],
            },
          }
          setAppV2(synthetic)
        } else {
          setAppV2(loaded)
        }
        setActiveTemplate(templateId)
      })
      .catch((err) => setError(err.message))
  }, [templateId, activeTemplate, isVerifyPage, isEmbedded, composeSpec])

  // Ref for gallery iframe — used to post NAVIGATE on popstate
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const [prevTemplateId, setPrevTemplateId] = useState(templateId)
  const [iframeSrc, setIframeSrc] = useState(() => {
    return withBase(currentRoute) + (typeof window !== "undefined" ? window.location.hash : "")
  })

  if (templateId !== prevTemplateId) {
    setPrevTemplateId(templateId)
    setIframeSrc(withBase(currentRoute) + (typeof window !== "undefined" ? window.location.hash : ""))
  }

  // Sync browser back/forward with internal route state
  useEffect(() => {
    const onPopState = () => {
      const newRoute = stripBase(window.location.pathname)
      setCurrentRoute(newRoute)

      // Sync gallery iframe with updated sub-route
      if (iframeRef.current?.contentWindow) {
        const prefix = resolvePrefix(newRoute)
        if (prefix) {
          const subRoute = stripPrefix(newRoute, prefix)
          iframeRef.current.contentWindow.postMessage(
            { type: 'IFRAME:NAVIGATE', payload: { route: subRoute } },
            '*'
          )
        }
      }
    }
    window.addEventListener("popstate", onPopState)
    return () => window.removeEventListener("popstate", onPopState)
  }, [])

  // Sync ROUTE_CHANGED from gallery iframe back to outer browser URL
  useEffect(() => {
    if (isEmbedded || !templateId) return
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type === 'IFRAME:ROUTE_CHANGED' && e.data?.payload?.route) {
        const innerRoute = e.data.payload.route as string
        const prefix = resolvePrefix(currentRoute) ?? `/templates/${templateId}`
        const fullRoute = prefix + (innerRoute === "/" ? "" : innerRoute)
        if (stripBase(window.location.pathname) !== fullRoute) {
          window.history.pushState(null, "", withBase(fullRoute))
          // Only update state if we navigated to a completely different template
          // or a non-template route, to prevent unnecessary iframe parent re-renders
          // that trigger iframe reloads and wipe out in-memory state.
          const newTemplateId = resolveTemplate(fullRoute)
          if (newTemplateId !== templateId) {
            setCurrentRoute(fullRoute)
          }
        }
      }
    }
    window.addEventListener("message", onMessage)
    return () => window.removeEventListener("message", onMessage)
  }, [isEmbedded, templateId, currentRoute])

  const routePrefix = useMemo(() => resolvePrefix(currentRoute), [currentRoute])

  const handleNavigate = useCallback((route: string) => {
    if (routePrefix) {
      // We're inside an app — routes are app-internal, always add prefix.
      // Must check routePrefix FIRST: internal routes like "/product/prod-1"
      // can collide with global TEMPLATE_MAP entries (e.g., "/product" → product-launch).
      const fullRoute = routePrefix + (route === "/" ? "" : route)
      setCurrentRoute(fullRoute)
      window.history.pushState(null, "", withBase(fullRoute))
    } else if (resolveTemplate(route)) {
      // Top-level navigation to a template route (e.g., "/fashion", "/blog")
      setCurrentRoute(route)
      window.history.pushState(null, "", withBase(route))
    } else {
      setCurrentRoute(route)
      window.history.pushState(null, "", withBase(route))
    }
    // SPA navigation doesn't auto-scroll to top like traditional page loads
    window.scrollTo(0, 0)
  }, [routePrefix])

  // v2 apps handle routing internally — only update browser URL, don't re-render
  const handleNavigateV2 = useCallback((route: string) => {
    const prefix = routePrefix ?? ""
    const fullRoute = prefix + (route === "/" ? "" : route)
    window.history.pushState(null, "", withBase(fullRoute))
    window.scrollTo(0, 0)
    // Gallery mode: when running inside the wrapper iframe, notify the parent so
    // it can sync the outer browser URL. Parent listens for IFRAME:ROUTE_CHANGED
    // and prepends its own prefix, so we send the inner app-relative route only.
    if (window.self !== window.top && window.parent) {
      try {
        window.parent.postMessage(
          { type: 'IFRAME:ROUTE_CHANGED', payload: { route } },
          '*',
        )
      } catch {
        // Cross-origin: silently fail; the iframe URL still updates locally.
      }
    }
  }, [routePrefix])

  const handleToast = useCallback((params: ToastParams) => {
    if (params.variant === "success") {
      toast.success(params.message, { description: params.description })
    } else if (params.variant === "error" || params.variant === "destructive") {
      toast.error(params.message, { description: params.description })
    } else {
      toast(params.message, { description: params.description })
    }
  }, [])

  const handleBackToGallery = useCallback(() => {
    setCurrentRoute("/")
    setAppV2(null)
    setActiveTemplate(null)
    window.history.pushState(null, "", withBase("/"))
  }, [])

  // Side-by-side template comparison
  if (isComparePage) {
    return (
      <Suspense fallback={<div className="flex h-screen items-center justify-center text-muted-foreground">Loading Compare...</div>}>
        <ComparePage />
      </Suspense>
    )
  }

  // Auth pages
  if (isAuthCallback) {
    return (
      <Suspense fallback={<div className="flex h-screen items-center justify-center bg-[#030014] text-white/40">Loading...</div>}>
        <AuthCallback />
      </Suspense>
    )
  }
  if (isLoginPage) {
    return (
      <Suspense fallback={<div className="flex h-screen items-center justify-center bg-[#030014] text-white/40">Loading...</div>}>
        <AuthPage />
      </Suspense>
    )
  }
  if (isOnboarding) {
    return (
      <Suspense fallback={<div className="flex h-screen items-center justify-center bg-[#030014] text-white/40">Loading...</div>}>
        <OnboardingPage />
      </Suspense>
    )
  }

  // Redirect to onboarding if authenticated but not onboarded
  // (skip for auth routes, onboarding page, and template preview routes)
  const initialTemplateId = resolveTemplate(stripBase(window.location.pathname))
  if (!authLoading && needsOnboarding && !isLoginPage && !isAuthCallback && !isOnboarding && !templateId && !initialTemplateId) {
    window.history.replaceState(null, "", withBase("/onboarding"))
    if (currentRoute !== "/onboarding") {
      setCurrentRoute("/onboarding")
    }
    return (
      <Suspense fallback={<div className="flex h-screen items-center justify-center bg-[#030014] text-white/40">Loading...</div>}>
        <OnboardingPage />
      </Suspense>
    )
  }

  // RN Preview removed — react-native-web/expo incompatible with Vite 8 Rolldown

  // Dev-only verification page
  if (isVerifyPage) {
    return (
      <Suspense fallback={<div className="flex h-screen items-center justify-center text-muted-foreground">Loading...</div>}>
        <VerifyPage />
      </Suspense>
    )
  }

  // Homepage with hero + featured templates
  if (isHomepage) {
    return (
      <Suspense fallback={<div className="flex h-screen items-center justify-center text-muted-foreground">Loading...</div>}>
        <HomePage onNavigate={handleNavigate} />
      </Suspense>
    )
  }

  // v4-reference mini-app — self-contained reference implementation of v4 schema
  if (isV4Reference) {
    const innerRoute = currentRoute.replace(/^\/v4-reference/, "") || "/home"
    return (
      <Suspense fallback={<div className="flex h-screen items-center justify-center text-muted-foreground">Loading...</div>}>
        <V4RefApp initialRoute={innerRoute} />
      </Suspense>
    )
  }

  // Phase A.2 measurement harness — /measure/:bundleId
  // Loads a single-block bundle from /apps/measure/<bundleId>/ and renders it
  // through AppV2. Playwright (Task 8) selects on data-testid="measure-ready".
  if (isMeasure) {
    if (!measureBundleId) {
      return (
        <div data-testid="measure-error" className="flex h-screen items-center justify-center text-destructive">
          Error: missing bundleId in /measure/:bundleId path
        </div>
      )
    }
    return (
      <Suspense fallback={<div data-testid="measure-suspense" className="flex h-screen items-center justify-center text-muted-foreground">Loading...</div>}>
        <MeasureRoute bundleId={measureBundleId} />
      </Suspense>
    )
  }

  // Template gallery
  if (isGallery) {
    return <TemplateLanding onNavigate={handleNavigate} />
  }

  // No template matched → show gallery
  if (!templateId) {
    return <TemplateLanding onNavigate={handleNavigate} />
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center text-destructive">
        Failed to load app: {error}
      </div>
    )
  }

  // ── Gallery mode (not embedded): render template in iframe for CSS isolation ──
  if (!isEmbedded) {
    // Find the display name from TEMPLATE_CATEGORIES
    const templateInfo = TEMPLATE_CATEGORIES
      .flatMap((c) => c.items)
      .find((t) => t.route === `/templates/${templateId}`)
    const appName = templateInfo?.label ?? templateId ?? ""

    return (
      <div className="flex flex-col h-screen">
        <div className="sticky top-0 z-[60] flex items-center gap-3 px-4 py-2 bg-background/95 backdrop-blur-md border-b border-border">
          <button
            onClick={handleBackToGallery}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <span className="text-sm font-medium text-foreground">{appName}</span>
        </div>
        <MemoizedIframe
          iframeRef={iframeRef}
          src={iframeSrc}
          title={appName}
        />
      </div>
    )
  }

  // ── Embedded mode (inside Studio iframe or nested iframe): render AppV2 directly ──
  if (!appV2) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        Loading...
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-screen">
      <div className="flex-1">
        <Suspense fallback={<div className="flex h-screen items-center justify-center text-muted-foreground">Loading...</div>}>
          <AppV2Lazy
            manifest={appV2.manifest}
            blocks={appV2.blocks}
            designSystem={appV2.designSystem}
            themeTokens={appV2.themeTokens}
            renderPackBytes={appV2.renderPackBytes}
            onReady={onRuntimeReady}
            onToast={handleToast}
            initialRoute={composeSpec ? "/compose" : (routePrefix ? stripPrefix(currentRoute, routePrefix) : currentRoute)}
            onNavigate={handleNavigateV2}
            pageSpec={composeSpec ?? undefined}
          />
        </Suspense>
      </div>
      <Suspense fallback={null}>
        <CogoAgentLazy
          apiKey={import.meta.env.VITE_GEMINI_API_KEY || ''}
          appId={appV2.manifest.appId}
          appName={appV2.manifest.name}
        />
      </Suspense>
    </div>
  )
}
